import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toolService from 'services/tools.service';

const STORAGE_KEY = 'dhbtools_selected_tool';

const ToolContext = createContext(null);

export function ToolProvider({ children }) {
    const [lstTool, setLstTool] = useState([]);
    const [selectedTool, setSelectedToolState] = useState('');
    const [selectedToolName, setSelectedToolName] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Load tools from API on mount
    useEffect(() => {
        toolService.getToolDatabase().then((result) => {
            const tools = result.data;
            setLstTool(tools);

            if (tools.length > 0) {
                // Try to restore from localStorage
                const savedData = localStorage.getItem(STORAGE_KEY);
                if (savedData) {
                    try {
                        const { tool, toolName } = JSON.parse(savedData);
                        // Verify the saved tool is still in the list
                        const found = tools.find(t => t.matool === tool);
                        if (found) {
                            setSelectedToolState(tool);
                            setSelectedToolName(toolName);
                            setIsLoading(false);
                            return;
                        }
                    } catch (e) {
                        console.error('Error parsing saved tool data:', e);
                    }
                }

                // Default to first tool if no valid saved data
                setSelectedToolState(tools[0].matool);
                setSelectedToolName(tools[0].name);
            }
            setIsLoading(false);
        }).catch(err => {
            console.error('Error loading tools:', err);
            setIsLoading(false);
        });
    }, []);

    // Set selected tool and save to localStorage
    const setSelectedTool = useCallback((tool, toolName, saveToStorage = true) => {
        setSelectedToolState(tool);
        setSelectedToolName(toolName);

        if (saveToStorage) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ tool, toolName }));
        }
    }, []);

    // Check if current tool is a special item (not in main list)
    const isSpecialTool = useCallback((tool) => {
        return !lstTool.some(t => t.matool === tool);
    }, [lstTool]);

    // Auto-select first tool if current selection is invalid for the current view
    const validateOrResetTool = useCallback(() => {
        if (lstTool.length > 0 && isSpecialTool(selectedTool)) {
            setSelectedTool(lstTool[0].matool, lstTool[0].name, true);
        }
    }, [lstTool, selectedTool, isSpecialTool, setSelectedTool]);

    return (
        <ToolContext.Provider value={{
            lstTool,
            selectedTool,
            selectedToolName,
            setSelectedTool,
            isLoading,
            isSpecialTool,
            validateOrResetTool
        }}>
            {children}
        </ToolContext.Provider>
    );
}

export function useToolContext() {
    const context = useContext(ToolContext);
    if (!context) {
        throw new Error('useToolContext must be used within a ToolProvider');
    }
    return context;
}

export default ToolContext;
